<template>
  <div>
    <div class="mb-5">
      <span class="subtitle ">
        {{ parentNodeTitle }}
      </span>
    </div>
    <VSelect
      v-model="node.nodeType"
      :label="$t('forms.labels.nodeType')"
      :items="availableTypes"
      :required="populateWith.empty"
      outlined
      itemValue="key"
      :disabled="disableNodeType || isEdition"
      data-cy="node-nodeType"
      @input="updateData"
    />
    <VAutocomplete
      ref="autocompleteRef"
      v-model="node.type"
      :label="$t('forms.labels.subnodeType')"
      :items="subNodeTypes"
      itemText="name"
      itemValue="id"
      :disabled="!node.nodeType"
      required
      data-cy="node-subNodeType"
      outlined
      @search="onSearchInput"
      @change="updateData"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VAutocomplete>
    <VTextField
      v-model="node.name"
      :label="$t('forms.labels.name')"
      outlined
      :rules="{ required: true, min: 1, max: 200 }"
      data-cy="node-name"
      @input="updateData"
    />
    <VTextField
      v-show="isNodeTypeContainer"
      v-model="node.description"
      outlined
      :required="isNodeTypeContainer"
      :label="$t('forms.labels.description')"
      :rules="{ max: 200 }"
      data-cy="node-description"
      @input="updateData"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import nodesTypeEnum from '@/utilities/enums/nodesTypes';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import lockService from '@/utilities/lockService';

import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import debounce from 'lodash/debounce';

export default {
    components: {
        VTextField,
        VSelect,
        VAutocomplete,
    },
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        parentNode: {
            type: Object,
            default: () => ({ empty: true }),
        },
        siteId: {
            type: String,
            default: '',
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        disableNodeType: {
            type: Boolean,
            default: false,
        },
        actionName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            node: {
                name: '',
                nodeType: '',
                type: '',
                description: null,
                controlerId: null,
                active: null,
            },
            pagination: {},
            queryParams: { page: 1, perpage: 25, search: '' },
            availableSubNodeTypes: [],
            onSearchInput: debounce(this.search, 500),
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
        ]),
        parentNodeTitle() {
            if (this.parentNode.empty) {
                return `${this.$t('sites.nodes.parentNode')}: ${this.$t('sites.nodes.noParentNode')}`;
            }
            const lowerCaseParentNodeType = this.$options.filters.lowercase(this.parentNode.nodeType);
            return `${this.$t('sites.nodes.parentNode')}: ${this.parentNode.name} (${this.$t(`sites.nodes.${lowerCaseParentNodeType}`)})`;
        },
        availableTypes() {
            let types = [];
            if (this.parentNode.nodeType === 'Container') {
                types = Object.keys(nodesTypeEnum).filter(node => node !== 'site' && node !== 'component');
            } else if (this.parentNode.nodeType === 'Network') {
                types = Object.keys(nodesTypeEnum).filter(node => node !== 'site' && node !== 'container');
            } else if (this.isEdition && this.node.nodeType !== 'component') {
                types = Object.keys(nodesTypeEnum).filter(node => node !== 'site' && node !== 'component');
            } else {
                types = Object.keys(nodesTypeEnum)
                    .filter(node => node !== 'site' && node !== 'component');
            }
            return types.map(node => ({ name: this.$t(`sites.nodes.${node}`), key: node }));
        },
        componentType() {
            return [{ name: this.$t('sites.nodes.component'), key: 'component' }];
        },
        isNodeTypeContainer() {
            return this.$options.filters.lowercase(this.node.nodeType) === 'container';
        },
        nodeType() {
            return this.node.type;
        },
        nodeSubType() {
            return this.node.nodeType;
        },
        subNodeTypes() {
            return this.availableSubNodeTypes.map(type => ({
                id: type.id,
                name: type.name[this.userLang] || type.name[this.teamMainLang],
            }));
        },
    },
    watch: {
        nodeSubType() {
            switch (this.node.nodeType) {
                case 'container':
                    this.resetQueryParams();
                    this.getContainerTypes();
                    break;
                case 'component':
                    this.resetQueryParams();
                    this.getComponentsTypes();
                    break;
                case 'network':
                    this.resetQueryParams();
                    this.getNetworkTypes();
                    break;
                default:
            }

            if (this.populateWith.empty) this.node.name = '';
        },
    },
    created() {
        if (this.isEdition) {
            this.node = { ...this.populateWith };
            this.node.nodeType = this.$options.filters.lowercase(this.populateWith.nodeType);
        }
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },

    beforeDestroy() {
        if (this.node && this.node.ref) {
            lockService.releaseLock(this.node.ref, this.actionName);
        }
        window.removeEventListener('beforeunload', this.handleUnload);
    },
    methods: {
        commonCallback(type) {
            return { ...type, name: type.name[this.userLang] || type.name[this.teamMainLang] };
        },
        handleUnload() {
            if (this.node && this.node.ref) {
                lockService.releaseLock(this.node.ref, this.actionName);
            }
        },
        async getNetworkTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getNetworkTypes({ pagination: false });
                this.availableSubNodeTypes = data.networkTypes;
            } else if (this.pagination?.currentPage !== this.queryParams?.page
            && this.pagination?.totalPageCount ? this.pagination?.totalPageCount >= this.queryParams?.page : true) {
                const { data } = await this.$rq.getNetworkTypes(this.queryParams);
                this.availableSubNodeTypes = [...data.networkTypes, ...this.availableSubNodeTypes];
                this.pagination = data.pagination;
            }
        },
        async getContainerTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getContainerTypes({ pagination: false });
                this.availableSubNodeTypes = data.containerTypes;
            } else if (this.pagination?.currentPage !== this.queryParams?.page
            && this.pagination?.totalPageCount ? this.pagination?.totalPageCount >= this.queryParams?.page : true) {
                const { data } = await this.$rq.getContainerTypes(this.queryParams);
                this.availableSubNodeTypes = [...data.containerTypes, ...this.availableSubNodeTypes];
                this.pagination = data.pagination;
            }
        },
        async getComponentsTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.availableSubNodeTypes = data.componentTypes;
            } else if (this.pagination?.currentPage !== this.queryParams?.page
            && this.pagination?.totalPageCount ? this.pagination?.totalPageCount >= this.queryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.queryParams);
                this.availableSubNodeTypes = [...data.componentTypes, ...this.availableSubNodeTypes];
                this.pagination = data.pagination;
            }
        },
        updateData() {
            let nodeTypeToRedirect = this.node.nodeType;
            // For editing nodetype (between network and container)
            if (this.isEdition && (this.node.nodeType.toLowerCase() !== this.populateWith.nodeType.toLowerCase())) {
                nodeTypeToRedirect = this.populateWith.nodeType.toLowerCase();
            }
            this.$emit('sendData', {
                node: {
                    ...this.node,
                    nodeType: this.$options.filters.capitalize(this.node.nodeType),
                    nodeTypeToRedirect,
                },
                parentNode: this.parentNode,
                siteId: this.siteId,
            });
        },
        resetQueryParams() {
            this.queryParams.page = 1;
            this.availableSubNodeTypes = [];
        },
        async search(search) {
            this.resetQueryParams();
            this.queryParams.search = search;
            this.queryParams.pagination = false;
            switch (this.node.nodeType) {
                case 'container':
                    await this.getContainerTypes();
                    break;
                case 'component':
                    await this.getComponentsTypes();
                    break;
                case 'network':
                    this.getNetworkTypes();
                    break;
                default:
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination?.currentPage < this.pagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                // await this.getProcedures();
                switch (this.node.nodeType) {
                    case 'container':
                        await this.getContainerTypes();
                        break;
                    case 'component':
                        await this.getComponentsTypes();
                        break;
                    case 'network':
                        this.getNetworkTypes();
                        break;
                    default:
                }
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
